// Base item catalogue. ADR 0016 §J / docs/04 §4.3 / §4.3.1 / §4.5.1.
//
// Static `ItemBase` master pool used by item generation, drop tables, and
// crafting. Rolled affixes / itemSkills / rarity / behaviorRule are layered
// on top at `Item` instantiation time (separate ADR scope).
//
// ID schema (ADR 0016 §J #1):
//   base.<slot>.<baseKind>.<tier>     e.g. `base.weaponMain.sword.5`
//
// Tier-1 baseline numbers are chosen so each baseKind hits a uniform DPS
// band at Tier 1 (≈ 10 dmg/s), differentiated only by attack interval.
// Tier scaling follows docs/04 §4.3.1 (`TIER_BASE_MULTIPLIER`):
//   Tier 1 = ×1.0 / Tier 2 = ×1.4 / Tier 3 = ×1.8 / Tier 4 = ×2.4 / Tier 5 = ×3.0
//
// `baseDmg` is `Math.round(baseDmgT1 * mult)`; per-tier Tier 5 / Tier 1
// ratio is exactly 3.0 by construction (verified in §J tests).
// `attackIntervalTicks` is tier-invariant (faster/slower archetype is
// expressed via baseDmg balancing instead).
//
// Phase 4 numeric calibration is out-of-scope (ADR 0016 §「未確定事項」).
import { seconds, TIER_BASE_MULTIPLIER, TIER_ILVL_RANGE, } from '@renavora/shared';
const TIERS = [1, 2, 3, 4, 5];
/**
 * Stat requirement per tier (linear-ish escalation). Tier 1 = 10, Tier 5 = 160.
 * The ratio is unconstrained by docs (open question for Phase 4 balancing),
 * but kept monotonic so §J #6 (Tier monotonicity) passes.
 */
const REQUIRED_STAT_BY_TIER = {
    1: 10,
    2: 20,
    3: 40,
    4: 80,
    5: 160,
};
const WEAPON_SPECS = [
    { kind: 'sword', affinity: ['MeleeDPS'], baseDmgT1: 10, intervalT1Seconds: 1.0, reqStatKey: 'str' },
    { kind: 'axe', affinity: ['MeleeDPS'], baseDmgT1: 12, intervalT1Seconds: 1.2, reqStatKey: 'str' },
    { kind: 'spear', affinity: ['MeleeDPS'], baseDmgT1: 14, intervalT1Seconds: 1.4, twoHanded: true, reqStatKey: 'str' },
    { kind: 'bow', affinity: ['RangedDPS'], baseDmgT1: 14, intervalT1Seconds: 1.4, twoHanded: true, reqStatKey: 'dex' },
    { kind: 'staff', affinity: ['MagicDPS'], baseDmgT1: 14, intervalT1Seconds: 1.4, twoHanded: true, reqStatKey: 'int' },
    { kind: 'dagger', affinity: ['MeleeDPS'], baseDmgT1: 6, intervalT1Seconds: 0.6, reqStatKey: 'dex' },
    { kind: 'mace', affinity: ['Healer'], baseDmgT1: 10, intervalT1Seconds: 1.0, reqStatKey: 'wis' },
    { kind: 'scythe', affinity: ['Summoner'], baseDmgT1: 14, intervalT1Seconds: 1.4, twoHanded: true, reqStatKey: 'int' },
    { kind: 'wand', affinity: ['MagicDPS'], baseDmgT1: 8, intervalT1Seconds: 0.8, reqStatKey: 'int' },
];
const WEAPON_SLOT = 'weaponMain';
function buildWeaponBase(spec, tier) {
    const mult = TIER_BASE_MULTIPLIER[tier];
    return {
        id: `base.${WEAPON_SLOT}.${spec.kind}.${tier}`,
        slot: WEAPON_SLOT,
        baseKind: spec.kind,
        tier,
        ilvlRange: TIER_ILVL_RANGE[tier],
        baseStats: {
            kind: 'weapon',
            baseDmg: Math.round(spec.baseDmgT1 * mult),
            attackIntervalTicks: seconds(spec.intervalT1Seconds),
            ...(spec.twoHanded ? { twoHanded: true } : {}),
        },
        typeAffinity: spec.affinity,
        requiredStats: { [spec.reqStatKey]: REQUIRED_STAT_BY_TIER[tier] },
    };
}
export const WEAPON_BASES = WEAPON_SPECS.flatMap((spec) => TIERS.map((tier) => buildWeaponBase(spec, tier)));
const ARMOR_SPECS = [
    { kind: 'helmet', slot: 'head', affinity: ['Tank', 'MeleeDPS'], armorT1: 5, hpBonusT1: 10, reqStatKey: 'str' },
    { kind: 'chest', slot: 'chest', affinity: ['Tank', 'Support'], armorT1: 8, hpBonusT1: 20, reqStatKey: 'str' },
    { kind: 'gloves', slot: 'hands', affinity: ['MeleeDPS', 'RangedDPS'], armorT1: 3, hpBonusT1: 5, reqStatKey: 'dex' },
    { kind: 'boots', slot: 'boots', affinity: ['RangedDPS', 'MagicDPS'], armorT1: 3, hpBonusT1: 5, reqStatKey: 'dex' },
    { kind: 'belt', slot: 'belt', affinity: ['Tank', 'Summoner'], armorT1: 2, hpBonusT1: 8, reqStatKey: 'str' },
    { kind: 'amulet', slot: 'amulet', affinity: ['Healer', 'MagicDPS'], armorT1: 1, hpBonusT1: 3, reqStatKey: 'int' },
    { kind: 'ring', slot: 'ring1', affinity: ['Tank', 'Healer'], armorT1: 1, hpBonusT1: 2, reqStatKey: 'wis' },
    { kind: 'ring', slot: 'ring2', affinity: ['Tank', 'Healer'], armorT1: 1, hpBonusT1: 2, reqStatKey: 'wis' },
    { kind: 'cloak', slot: 'cloak', affinity: ['RangedDPS', 'Support'], armorT1: 2, hpBonusT1: 5, reqStatKey: 'dex' },
];
function buildArmorBase(spec, tier) {
    const mult = TIER_BASE_MULTIPLIER[tier];
    return {
        id: `base.${spec.slot}.${spec.kind}.${tier}`,
        slot: spec.slot,
        baseKind: spec.kind,
        tier,
        ilvlRange: TIER_ILVL_RANGE[tier],
        baseStats: {
            kind: 'armor',
            armor: Math.round(spec.armorT1 * mult),
            hpBonus: Math.round(spec.hpBonusT1 * mult),
        },
        typeAffinity: spec.affinity,
        requiredStats: { [spec.reqStatKey]: REQUIRED_STAT_BY_TIER[tier] },
    };
}
export const ARMOR_BASES = ARMOR_SPECS.flatMap((spec) => TIERS.map((tier) => buildArmorBase(spec, tier)));
const OFF_HAND_SPECS = [
    { kind: 'shield', affinity: ['Tank', 'Support'], armorT1: 6, reqStatKey: 'str' },
    { kind: 'focus', affinity: ['MagicDPS', 'Healer'], spellPowerT1: 5, reqStatKey: 'int' },
    { kind: 'quiver', affinity: ['RangedDPS'], rangedPowerT1: 5, reqStatKey: 'dex' },
];
const OFF_HAND_SLOT = 'offHand';
function buildOffHandBase(spec, tier) {
    const mult = TIER_BASE_MULTIPLIER[tier];
    return {
        id: `base.${OFF_HAND_SLOT}.${spec.kind}.${tier}`,
        slot: OFF_HAND_SLOT,
        baseKind: spec.kind,
        tier,
        ilvlRange: TIER_ILVL_RANGE[tier],
        baseStats: {
            kind: 'offHand',
            ...(spec.armorT1 !== undefined ? { armor: Math.round(spec.armorT1 * mult) } : {}),
            ...(spec.spellPowerT1 !== undefined ? { spellPower: Math.round(spec.spellPowerT1 * mult) } : {}),
            ...(spec.rangedPowerT1 !== undefined ? { rangedPower: Math.round(spec.rangedPowerT1 * mult) } : {}),
        },
        typeAffinity: spec.affinity,
        requiredStats: { [spec.reqStatKey]: REQUIRED_STAT_BY_TIER[tier] },
    };
}
export const OFF_HAND_BASES = OFF_HAND_SPECS.flatMap((spec) => TIERS.map((tier) => buildOffHandBase(spec, tier)));
/** Full master pool: weapons (45) + armor (45) + off-hand (15) = 105 bases. */
export const ITEM_BASES = [...WEAPON_BASES, ...ARMOR_BASES, ...OFF_HAND_BASES];
//# sourceMappingURL=base-items.js.map